const CSV_URL = "https://firebasestorage.googleapis.com/v0/b/stipendee.firebasestorage.app/o/osservatorio%2Fdataset.csv?alt=media";
const GEOJSON_URL = new URL("../data/italy-regions.geojson", import.meta.url).toString();
const BIN_SCALES = [
  { max: 50000, step: 5000 },
  { max: 100000, step: 10000 },
  { max: 200000, step: 20000 },
  { max: Infinity, step: 50000 }
];
const MIN_SAMPLE = 3;
const TRIM_LOWER = 0.005;
const TRIM_UPPER = 0.995;
const MIN_RAL = 5000;
const LIVELLO_ORDER = ["Operaio", "Impiegato", "Quadro", "Dirigente"];
const DIMENSIONE_ORDER = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1001-5000",
  "Oltre 5000"
];
const ETA_ORDER = ["16-18", "18-25", "26-30", "31-35", "36-45", "46-55", "56-65", "66+"];
const ESPERIENZA_ORDER = ["0-1", "2-5", "6-10", "11-20", "21+"];
const TITOLO_STUDIO_ORDER = [
  "Altro",
  "Diploma",
  "Laurea triennale",
  "Laurea magistrale",
  "Master / MBA",
  "PhD / Dottorato"
];
const SENIORITY_ORDER = [
  "Entry-Level",
  "Junior",
  "Mid-Level",
  "Senior",
  "Lead",
  "Team Lead",
  "Lead / Team Lead",
  "Manager",
  "Director / VP",
  "Executive / C-Level"
];

const state = {
  raw: [],
  filtered: [],
  charts: {},
  geo: null,
  chartReady: new Set()
};

const currency = new Intl.NumberFormat("it-IT", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const numberFormat = new Intl.NumberFormat("it-IT", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1
});

const countFormat = new Intl.NumberFormat("it-IT", {
  maximumFractionDigits: 0
});

function normalizeValue(value) {
  if (value === null || value === undefined) return "";
  const stringValue = String(value).trim();
  return stringValue === "" ? "" : stringValue;
}

function parseRal(value) {
  const numeric = Number(String(value).replace(/[^0-9.]/g, ""));
  return Number.isFinite(numeric) ? numeric : null;
}

function parseScale(value) {
  const numeric = Number(String(value).replace(/[^0-9.]/g, ""));
  return Number.isFinite(numeric) ? numeric : null;
}

function scaleToPercent(value) {
  if (value === null || value === undefined) return null;
  return (value / 5) * 100;
}

function formatDate(value) {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  return date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function setChartEmpty(canvasId, isEmpty, message = "Campione insufficiente") {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;
  const panel = canvas.closest(".panel");
  if (!panel) return;
  let messageEl = panel.querySelector(".chart-empty-message");
  if (!messageEl) {
    messageEl = document.createElement("div");
    messageEl.className = "chart-empty-message";
    panel.appendChild(messageEl);
  }
  messageEl.textContent = message;
  panel.classList.toggle("is-empty", isEmpty);
}

function setOptions(select, values) {
  const options = ["", ...values];
  select.innerHTML = "";
  options.forEach((value) => {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = value === "" ? "Tutti" : value;
    select.appendChild(option);
  });
}

function setOptionsOrdered(select, order, values) {
  const ordered = [
    ...order.filter((value) => values.includes(value)),
    ...values.filter((value) => !order.includes(value))
  ];
  setOptions(select, ordered);
}

function uniqueValues(data, field) {
  const set = new Set();
  data.forEach((row) => {
    const value = normalizeValue(row[field]);
    if (value) set.add(value);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, "it"));
}

function computeStats(values, trim = true) {
  const source = trim ? trimOutliers(values, TRIM_LOWER, TRIM_UPPER) : values;
  const numeric = source.filter((value) => typeof value === "number").sort((a, b) => a - b);
  if (numeric.length < MIN_SAMPLE) {
    return { avg: null, median: null, min: null, max: null };
  }
  const sum = numeric.reduce((acc, val) => acc + val, 0);
  const mid = Math.floor(numeric.length / 2);
  const median = numeric.length % 2 ? numeric[mid] : (numeric[mid - 1] + numeric[mid]) / 2;
  return {
    avg: sum / numeric.length,
    median,
    min: numeric[0],
    max: numeric[numeric.length - 1]
  };
}

function trimOutliers(values, lowerPercent = TRIM_LOWER, upperPercent = TRIM_UPPER, minValue = null) {
  const numeric = values
    .filter((value) => typeof value === "number")
    .filter((value) => (minValue === null ? true : value >= minValue))
    .sort((a, b) => a - b);
  if (numeric.length < MIN_SAMPLE) return [];
  let lowerIndex = Math.floor(numeric.length * lowerPercent);
  let upperIndex = Math.ceil(numeric.length * upperPercent) - 1;
  if (numeric.length >= MIN_SAMPLE + 2) {
    if (lowerIndex === 0) lowerIndex = 1;
    if (upperIndex >= numeric.length - 1) upperIndex = numeric.length - 2;
  }
  const start = Math.max(0, lowerIndex);
  const end = Math.min(numeric.length - 1, upperIndex);
  return numeric.slice(start, end + 1);
}

function trimRal(values) {
  return trimOutliers(values, TRIM_LOWER, TRIM_UPPER, MIN_RAL);
}

function buildVariableBins(values) {
  const numeric = values.filter((value) => typeof value === "number" && value >= 0);
  if (!numeric.length) {
    return { labels: [], counts: [] };
  }

  const maxValue = Math.max(...numeric);
  const bins = [];
  let start = 0;
  let scaleIndex = 0;

  while (start <= maxValue) {
    const scale = BIN_SCALES[scaleIndex] || BIN_SCALES[BIN_SCALES.length - 1];
    const step = scale.step;
    const end = Math.min(start + step, scale.max);
    bins.push({ start, end });
    start = end;
    if (start >= scale.max && scaleIndex < BIN_SCALES.length - 1) {
      scaleIndex += 1;
    }
    if (scale.max === Infinity && start >= maxValue) {
      break;
    }
  }

  const counts = new Array(bins.length).fill(0);
  numeric.forEach((value) => {
    const binIndex = bins.findIndex((bin) => value >= bin.start && value < bin.end);
    if (binIndex >= 0) counts[binIndex] += 1;
  });

  const labels = bins.map((bin) => `${Math.round(bin.start / 1000)}k-${Math.round(bin.end / 1000)}k`);
  const filtered = labels
    .map((label, index) => ({ label, count: counts[index] }))
    .filter((entry) => entry.count >= MIN_SAMPLE);
  return {
    labels: filtered.map((entry) => entry.label),
    counts: filtered.map((entry) => entry.count)
  };
}

function buildCategoryCounts(data, field, options = {}) {
  const counts = {};
  const unknownLabel = options.unknownLabel || "Non specificato";
  const minCount = options.minCount || 1;

  data.forEach((row) => {
    const rawValue = normalizeValue(row[field]);
    const value = rawValue || unknownLabel;
    counts[value] = (counts[value] || 0) + 1;
  });

  let labels;
  if (options.order && options.order.length) {
    const ordered = options.order.filter((value) => counts[value]);
    const remaining = Object.keys(counts)
      .filter((value) => !options.order.includes(value))
      .sort((a, b) => counts[b] - counts[a]);
    labels = [...ordered, ...remaining];
  } else {
    labels = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  }

  if (options.limit && labels.length > options.limit) {
    const top = labels.slice(0, options.limit);
    const rest = labels.slice(options.limit);
    const restCount = rest.reduce((acc, label) => acc + counts[label], 0);
    if (restCount >= minCount) {
      counts.Altro = restCount;
      top.push("Altro");
    }
    labels = top;
  }

  labels = labels.filter((label) => counts[label] >= minCount);

  return {
    labels,
    data: labels.map((label) => counts[label] || 0)
  };
}

function updateCategoryChart(key, canvasId, labels, data, color, options = {}) {
  const indexAxis = options.indexAxis || "x";
  const yCurrency = options.yCurrency || false;
  const datasetLabel = options.datasetLabel || "Risposte";
  const yScale = options.yScale || {};
  const transform = options.transform || null;
  const rawData = options.rawData || null;
  const isLog = yScale.type === "logarithmic";
  if (!state.charts[key]) {
    state.charts[key] = new Chart(document.getElementById(canvasId), {
      type: "bar",
      data: {
        labels,
        datasets: [{
          label: datasetLabel,
          data,
          rawData,
          backgroundColor: color.background,
          borderColor: color.border,
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        indexAxis,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = context.dataset.rawData ? context.dataset.rawData[context.dataIndex] : context.parsed.y;
                if (yCurrency) {
                  return `${datasetLabel}: ${currency.format(value)}`;
                }
                return `${datasetLabel}: ${value}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257" },
            beginAtZero: true
          },
          y: {
            type: yScale.type || "linear",
            min: yScale.min,
            max: yScale.max,
            ticks: yCurrency
              ? {
                  color: "#6b6257",
                  callback: (value) => currency.format(value)
                }
              : {
                  color: "#6b6257",
                  callback: (value) => {
                    if (transform === "sqrt") {
                      return Math.round(value * value);
                    }
                    return isLog && Number.isInteger(value) ? value : value;
                  }
                },
            beginAtZero: !isLog
          }
        }
      }
    });
  } else {
    state.charts[key].data.labels = labels;
    state.charts[key].data.datasets[0].data = data;
    state.charts[key].data.datasets[0].label = datasetLabel;
    state.charts[key].data.datasets[0].rawData = rawData;
    state.charts[key].update();
  }
}

function updateCharts(filtered) {
  const isReady = (id) => state.chartReady.has(id);
  const ralValues = filtered.map((row) => parseRal(row.ral)).filter((value) => value !== null);
  const ralTrimmed = trimRal(ralValues);
  const { labels, counts } = buildVariableBins(ralTrimmed);
  const hasRalData = labels.length > 0;
  if (isReady("ral-chart")) {
    setChartEmpty("ral-chart", !hasRalData);
    if (!hasRalData) {
      if (state.charts.ral) {
        state.charts.ral.destroy();
        state.charts.ral = null;
      }
    }
  }

  if (isReady("ral-chart") && hasRalData && !state.charts.ral) {
    state.charts.ral = new Chart(document.getElementById("ral-chart"), {
      type: "bar",
      data: {
        labels,
        datasets: [{
          label: "Risposte",
          data: counts,
          rawCounts: counts,
          backgroundColor: "rgba(255, 138, 0, 0.6)",
          borderColor: "#ff8a00",
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => {
                const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                return `Risposte: ${countFormat.format(raw)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257" }
          },
          y: {
            ticks: { color: "#6b6257" },
            beginAtZero: true
          }
        }
      }
    });
  } else if (isReady("ral-chart") && hasRalData && state.charts.ral) {
    state.charts.ral.data.labels = labels;
    state.charts.ral.data.datasets[0].data = counts;
    state.charts.ral.data.datasets[0].rawCounts = counts;
    state.charts.ral.update();
  }

  const seniorityBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row.seniority) || "Non specificato";
    const ralValue = parseRal(row.ral);
    if (!seniorityBuckets[value]) {
      seniorityBuckets[value] = [];
    }
    if (ralValue !== null) {
      seniorityBuckets[value].push(ralValue);
    }
  });
  const seniorityLabels = [
    ...SENIORITY_ORDER.filter((value) => seniorityBuckets[value]?.length >= MIN_SAMPLE),
    ...Object.keys(seniorityBuckets).filter(
      (value) => !SENIORITY_ORDER.includes(value) && seniorityBuckets[value]?.length >= MIN_SAMPLE
    )
  ];
  const seniorityTrimmed = seniorityLabels.map((label) => trimRal(seniorityBuckets[label]));
  const seniorityCounts = seniorityTrimmed.map((values) => values.length);
  const seniorityAvg = seniorityTrimmed.map((values) => computeStats(values, false).avg);
  const seniorityMedian = seniorityTrimmed.map((values) => computeStats(values, false).median);
  const hasSeniority = seniorityLabels.length > 0;
  if (isReady("seniority-chart")) {
    setChartEmpty("seniority-chart", !hasSeniority);
    if (!hasSeniority) {
      if (state.charts.seniority) {
        state.charts.seniority.destroy();
        state.charts.seniority = null;
      }
    }
  }

  if (isReady("seniority-chart") && hasSeniority && !state.charts.seniority) {
    state.charts.seniority = new Chart(document.getElementById("seniority-chart"), {
      type: "bar",
      data: {
        labels: seniorityLabels,
        datasets: [
          {
            type: "bar",
            label: "Risposte",
            data: seniorityCounts,
            rawCounts: seniorityCounts,
            backgroundColor: "rgba(33, 76, 205, 0.35)",
            borderColor: "#214ccd",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            type: "line",
            label: "RAL media",
            data: seniorityAvg,
            borderColor: "#0b7a75",
            backgroundColor: "rgba(11, 122, 117, 0.15)",
            pointBackgroundColor: "#0b7a75",
            tension: 0.3,
            yAxisID: "yRal"
          },
          {
            type: "line",
            label: "RAL mediana",
            data: seniorityMedian,
            borderColor: "#ff8a00",
            backgroundColor: "rgba(255, 138, 0, 0.15)",
            pointBackgroundColor: "#ff8a00",
            tension: 0.3,
            yAxisID: "yRal"
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === "Risposte") {
                  const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                  return `Risposte: ${countFormat.format(raw)}`;
                }
                return `${context.dataset.label}: ${currency.format(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257" }
          },
          y: {
            ticks: { color: "#6b6257" },
            beginAtZero: true,
            title: { display: true, text: "Risposte" }
          },
          yRal: {
            position: "right",
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            grid: { drawOnChartArea: false },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("seniority-chart") && hasSeniority && state.charts.seniority) {
    state.charts.seniority.data.labels = seniorityLabels;
    state.charts.seniority.data.datasets[0].data = seniorityCounts;
    state.charts.seniority.data.datasets[0].rawCounts = seniorityCounts;
    state.charts.seniority.data.datasets[1].data = seniorityAvg;
    state.charts.seniority.data.datasets[2].data = seniorityMedian;
    state.charts.seniority.update();
  }

  const genereOrder = ["Uomo", "Donna", "Non binario", "Preferisco non dire"];
  const genereBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row.genere);
    if (!value) return;
    const ralValue = parseRal(row.ral);
    if (ralValue === null) return;
    if (!genereBuckets[value]) {
      genereBuckets[value] = [];
    }
    genereBuckets[value].push(ralValue);
  });
  const genereLabels = [
    ...genereOrder.filter((value) => genereBuckets[value]?.length >= MIN_SAMPLE),
    ...Object.keys(genereBuckets).filter(
      (value) => !genereOrder.includes(value) && genereBuckets[value]?.length >= MIN_SAMPLE
    )
  ];
  const genereTrimmed = genereLabels.map((label) => trimRal(genereBuckets[label] || []));
  const genereData = genereTrimmed.map((values) => {
    if (values.length < MIN_SAMPLE) return null;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return sum / values.length;
  });
  const genereLabelsFiltered = genereLabels.filter((_, index) => genereTrimmed[index].length >= MIN_SAMPLE);
  const genereDataFiltered = genereData.filter((value) => value !== null);
  const hasGenere = genereLabelsFiltered.length > 0;
  if (isReady("genere-chart")) {
    setChartEmpty("genere-chart", !hasGenere);
    if (!hasGenere) {
      if (state.charts.genere) {
        state.charts.genere.destroy();
        state.charts.genere = null;
      }
    }
  }
  if (isReady("genere-chart") && hasGenere) {
    updateCategoryChart(
      "genere",
      "genere-chart",
      genereLabelsFiltered,
      genereDataFiltered,
      { background: "rgba(9, 105, 106, 0.6)", border: "#09696a" },
      { yCurrency: true, datasetLabel: "RAL media" }
    );
  }

  const rapportoBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row.rapporto);
    if (!value) return;
    const ralValue = parseRal(row.ral);
    if (!rapportoBuckets[value]) {
      rapportoBuckets[value] = [];
    }
    if (ralValue !== null) {
      rapportoBuckets[value].push(ralValue);
    }
  });
  const rapportoLabels = Object.keys(rapportoBuckets)
    .filter((label) => rapportoBuckets[label].length >= MIN_SAMPLE)
    .sort((a, b) => rapportoBuckets[b].length - rapportoBuckets[a].length);
  const rapportoTrimmed = rapportoLabels.map((label) => trimRal(rapportoBuckets[label]));
  const rapportoCounts = rapportoTrimmed.map((values) => values.length);
  const rapportoCountsCompressed = rapportoCounts.map((value) => Math.sqrt(value));
  const rapportoAvg = rapportoTrimmed.map((values) => computeStats(values, false).avg);
  const rapportoMedian = rapportoTrimmed.map((values) => computeStats(values, false).median);
  const hasRapporto = rapportoLabels.length > 0;
  if (isReady("rapporto-chart")) {
    setChartEmpty("rapporto-chart", !hasRapporto);
    if (!hasRapporto) {
      if (state.charts.rapporto) {
        state.charts.rapporto.destroy();
        state.charts.rapporto = null;
      }
    }
  }

  if (isReady("rapporto-chart") && hasRapporto && !state.charts.rapporto) {
    state.charts.rapporto = new Chart(document.getElementById("rapporto-chart"), {
      type: "bar",
      data: {
        labels: rapportoLabels,
        datasets: [
          {
            label: "Risposte",
            data: rapportoCountsCompressed,
            rawCounts: rapportoCounts,
            backgroundColor: "rgba(33, 76, 205, 0.35)",
            borderColor: "#214ccd",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: "RAL media",
            data: rapportoAvg,
            borderColor: "#0b7a75",
            backgroundColor: "rgba(11, 122, 117, 0.15)",
            pointBackgroundColor: "#0b7a75",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          },
          {
            label: "RAL mediana",
            data: rapportoMedian,
            borderColor: "#ff8a00",
            backgroundColor: "rgba(255, 138, 0, 0.15)",
            pointBackgroundColor: "#ff8a00",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === "Risposte") {
                  const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                  return `Risposte: ${countFormat.format(raw)}`;
                }
                return `${context.dataset.label}: ${currency.format(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257" }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => Math.round(value * value)
            },
            beginAtZero: true,
            title: { display: true, text: "Risposte" }
          },
          yRal: {
            position: "right",
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            grid: { drawOnChartArea: false },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("rapporto-chart") && hasRapporto && state.charts.rapporto) {
    state.charts.rapporto.data.labels = rapportoLabels;
    state.charts.rapporto.data.datasets[0].data = rapportoCountsCompressed;
    state.charts.rapporto.data.datasets[0].rawCounts = rapportoCounts;
    state.charts.rapporto.data.datasets[1].data = rapportoAvg;
    state.charts.rapporto.data.datasets[2].data = rapportoMedian;
    state.charts.rapporto.update();
  }

  const modalitaBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row["modalita-lavoro"]);
    if (!value) return;
    const ralValue = parseRal(row.ral);
    if (!modalitaBuckets[value]) {
      modalitaBuckets[value] = [];
    }
    if (ralValue !== null) {
      modalitaBuckets[value].push(ralValue);
    }
  });
  const modalitaLabels = Object.keys(modalitaBuckets)
    .filter((label) => modalitaBuckets[label].length >= MIN_SAMPLE)
    .sort((a, b) => modalitaBuckets[b].length - modalitaBuckets[a].length);
  const modalitaTrimmed = modalitaLabels.map((label) => trimRal(modalitaBuckets[label]));
  const modalitaCounts = modalitaTrimmed.map((values) => values.length);
  const modalitaCountsCompressed = modalitaCounts.map((value) => Math.sqrt(value));
  const modalitaAvg = modalitaTrimmed.map((values) => computeStats(values, false).avg);
  const modalitaMedian = modalitaTrimmed.map((values) => computeStats(values, false).median);
  const hasModalita = modalitaLabels.length > 0;
  if (isReady("modalita-chart")) {
    setChartEmpty("modalita-chart", !hasModalita);
    if (!hasModalita) {
      if (state.charts.modalita) {
        state.charts.modalita.destroy();
        state.charts.modalita = null;
      }
    }
  }

  if (isReady("modalita-chart") && hasModalita && !state.charts.modalita) {
    state.charts.modalita = new Chart(document.getElementById("modalita-chart"), {
      type: "bar",
      data: {
        labels: modalitaLabels,
        datasets: [
          {
            label: "Risposte",
            data: modalitaCountsCompressed,
            rawCounts: modalitaCounts,
            backgroundColor: "rgba(255, 99, 132, 0.35)",
            borderColor: "#ff6384",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: "RAL media",
            data: modalitaAvg,
            borderColor: "#0b7a75",
            backgroundColor: "rgba(11, 122, 117, 0.15)",
            pointBackgroundColor: "#0b7a75",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          },
          {
            label: "RAL mediana",
            data: modalitaMedian,
            borderColor: "#ff8a00",
            backgroundColor: "rgba(255, 138, 0, 0.15)",
            pointBackgroundColor: "#ff8a00",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === "Risposte") {
                  const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                  return `Risposte: ${countFormat.format(raw)}`;
                }
                return `${context.dataset.label}: ${currency.format(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257" }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => Math.round(value * value)
            },
            beginAtZero: true,
            title: { display: true, text: "Risposte" }
          },
          yRal: {
            position: "right",
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            grid: { drawOnChartArea: false },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("modalita-chart") && hasModalita && state.charts.modalita) {
    state.charts.modalita.data.labels = modalitaLabels;
    state.charts.modalita.data.datasets[0].data = modalitaCountsCompressed;
    state.charts.modalita.data.datasets[0].rawCounts = modalitaCounts;
    state.charts.modalita.data.datasets[1].data = modalitaAvg;
    state.charts.modalita.data.datasets[2].data = modalitaMedian;
    state.charts.modalita.update();
  }

  const etaBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row.eta);
    if (!value) return;
    const ralValue = parseRal(row.ral);
    if (!etaBuckets[value]) {
      etaBuckets[value] = [];
    }
    if (ralValue !== null) {
      etaBuckets[value].push(ralValue);
    }
  });
  const etaLabels = [
    ...ETA_ORDER.filter((label) => etaBuckets[label]?.length >= MIN_SAMPLE),
    ...Object.keys(etaBuckets).filter(
      (label) => !ETA_ORDER.includes(label) && etaBuckets[label]?.length >= MIN_SAMPLE
    )
  ];
  const etaTrimmed = etaLabels.map((label) => trimRal(etaBuckets[label]));
  const etaCounts = etaTrimmed.map((values) => values.length);
  const etaCountsCompressed = etaCounts.map((value) => Math.sqrt(value));
  const etaAvg = etaTrimmed.map((values) => computeStats(values, false).avg);
  const etaMedian = etaTrimmed.map((values) => computeStats(values, false).median);
  const hasEta = etaLabels.length > 0;
  if (isReady("eta-chart")) {
    setChartEmpty("eta-chart", !hasEta);
    if (!hasEta) {
      if (state.charts.eta) {
        state.charts.eta.destroy();
        state.charts.eta = null;
      }
    }
  }

  if (isReady("eta-chart") && hasEta && !state.charts.eta) {
    state.charts.eta = new Chart(document.getElementById("eta-chart"), {
      type: "bar",
      data: {
        labels: etaLabels,
        datasets: [
          {
            label: "Risposte",
            data: etaCountsCompressed,
            rawCounts: etaCounts,
            backgroundColor: "rgba(59, 130, 246, 0.35)",
            borderColor: "#3b82f6",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: "RAL media",
            data: etaAvg,
            borderColor: "#0b7a75",
            backgroundColor: "rgba(11, 122, 117, 0.15)",
            pointBackgroundColor: "#0b7a75",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          },
          {
            label: "RAL mediana",
            data: etaMedian,
            borderColor: "#ff8a00",
            backgroundColor: "rgba(255, 138, 0, 0.15)",
            pointBackgroundColor: "#ff8a00",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === "Risposte") {
                  const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                  return `Risposte: ${countFormat.format(raw)}`;
                }
                return `${context.dataset.label}: ${currency.format(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257" }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => Math.round(value * value)
            },
            beginAtZero: true,
            title: { display: true, text: "Risposte" }
          },
          yRal: {
            position: "right",
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            grid: { drawOnChartArea: false },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("eta-chart") && hasEta && state.charts.eta) {
    state.charts.eta.data.labels = etaLabels;
    state.charts.eta.data.datasets[0].data = etaCountsCompressed;
    state.charts.eta.data.datasets[0].rawCounts = etaCounts;
    state.charts.eta.data.datasets[1].data = etaAvg;
    state.charts.eta.data.datasets[2].data = etaMedian;
    state.charts.eta.update();
  }

  const esperienzaBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row.esperienza);
    if (!value) return;
    const ralValue = parseRal(row.ral);
    if (!esperienzaBuckets[value]) {
      esperienzaBuckets[value] = [];
    }
    if (ralValue !== null) {
      esperienzaBuckets[value].push(ralValue);
    }
  });
  const esperienzaLabels = [
    ...ESPERIENZA_ORDER.filter((label) => esperienzaBuckets[label]?.length >= MIN_SAMPLE),
    ...Object.keys(esperienzaBuckets).filter(
      (label) => !ESPERIENZA_ORDER.includes(label) && esperienzaBuckets[label]?.length >= MIN_SAMPLE
    )
  ];
  const esperienzaTrimmed = esperienzaLabels.map((label) => trimRal(esperienzaBuckets[label]));
  const esperienzaCounts = esperienzaTrimmed.map((values) => values.length);
  const esperienzaCountsCompressed = esperienzaCounts.map((value) => Math.sqrt(value));
  const esperienzaAvg = esperienzaTrimmed.map((values) => computeStats(values, false).avg);
  const esperienzaMedian = esperienzaTrimmed.map((values) => computeStats(values, false).median);
  const hasEsperienza = esperienzaLabels.length > 0;
  if (isReady("esperienza-chart")) {
    setChartEmpty("esperienza-chart", !hasEsperienza);
    if (!hasEsperienza) {
      if (state.charts.esperienza) {
        state.charts.esperienza.destroy();
        state.charts.esperienza = null;
      }
    }
  }

  if (isReady("esperienza-chart") && hasEsperienza && !state.charts.esperienza) {
    state.charts.esperienza = new Chart(document.getElementById("esperienza-chart"), {
      type: "bar",
      data: {
        labels: esperienzaLabels,
        datasets: [
          {
            label: "Risposte",
            data: esperienzaCountsCompressed,
            rawCounts: esperienzaCounts,
            backgroundColor: "rgba(16, 185, 129, 0.35)",
            borderColor: "#10b981",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: "RAL media",
            data: esperienzaAvg,
            borderColor: "#0b7a75",
            backgroundColor: "rgba(11, 122, 117, 0.15)",
            pointBackgroundColor: "#0b7a75",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          },
          {
            label: "RAL mediana",
            data: esperienzaMedian,
            borderColor: "#ff8a00",
            backgroundColor: "rgba(255, 138, 0, 0.15)",
            pointBackgroundColor: "#ff8a00",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === "Risposte") {
                  const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                  return `Risposte: ${countFormat.format(raw)}`;
                }
                return `${context.dataset.label}: ${currency.format(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257" }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => Math.round(value * value)
            },
            beginAtZero: true,
            title: { display: true, text: "Risposte" }
          },
          yRal: {
            position: "right",
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            grid: { drawOnChartArea: false },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("esperienza-chart") && hasEsperienza && state.charts.esperienza) {
    state.charts.esperienza.data.labels = esperienzaLabels;
    state.charts.esperienza.data.datasets[0].data = esperienzaCountsCompressed;
    state.charts.esperienza.data.datasets[0].rawCounts = esperienzaCounts;
    state.charts.esperienza.data.datasets[1].data = esperienzaAvg;
    state.charts.esperienza.data.datasets[2].data = esperienzaMedian;
    state.charts.esperienza.update();
  }

  const titoloBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row["titolo-studio"]);
    if (!value) return;
    const ralValue = parseRal(row.ral);
    if (!titoloBuckets[value]) {
      titoloBuckets[value] = [];
    }
    if (ralValue !== null) {
      titoloBuckets[value].push(ralValue);
    }
  });
  const titoloLabels = [
    ...TITOLO_STUDIO_ORDER.filter((label) => titoloBuckets[label]?.length >= MIN_SAMPLE),
    ...Object.keys(titoloBuckets).filter(
      (label) => !TITOLO_STUDIO_ORDER.includes(label) && titoloBuckets[label]?.length >= MIN_SAMPLE
    )
  ];
  const titoloTrimmed = titoloLabels.map((label) => trimRal(titoloBuckets[label]));
  const titoloCounts = titoloTrimmed.map((values) => values.length);
  const titoloCountsCompressed = titoloCounts.map((value) => Math.sqrt(value));
  const titoloAvg = titoloTrimmed.map((values) => computeStats(values, false).avg);
  const titoloMedian = titoloTrimmed.map((values) => computeStats(values, false).median);
  const hasTitolo = titoloLabels.length > 0;
  if (isReady("titolo-studio-chart")) {
    setChartEmpty("titolo-studio-chart", !hasTitolo);
    if (!hasTitolo) {
      if (state.charts.titoloStudio) {
        state.charts.titoloStudio.destroy();
        state.charts.titoloStudio = null;
      }
    }
  }

  if (isReady("titolo-studio-chart") && hasTitolo && !state.charts.titoloStudio) {
    state.charts.titoloStudio = new Chart(document.getElementById("titolo-studio-chart"), {
      type: "bar",
      data: {
        labels: titoloLabels,
        datasets: [
          {
            label: "Risposte",
            data: titoloCountsCompressed,
            rawCounts: titoloCounts,
            backgroundColor: "rgba(33, 76, 205, 0.35)",
            borderColor: "#214ccd",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: "RAL media",
            data: titoloAvg,
            borderColor: "#0b7a75",
            backgroundColor: "rgba(11, 122, 117, 0.15)",
            pointBackgroundColor: "#0b7a75",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          },
          {
            label: "RAL mediana",
            data: titoloMedian,
            borderColor: "#ff8a00",
            backgroundColor: "rgba(255, 138, 0, 0.15)",
            pointBackgroundColor: "#ff8a00",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === "Risposte") {
                  const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                  return `Risposte: ${countFormat.format(raw)}`;
                }
                return `${context.dataset.label}: ${currency.format(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257", maxRotation: 45, minRotation: 45 }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => Math.round(value * value)
            },
            beginAtZero: true,
            title: { display: true, text: "Risposte" }
          },
          yRal: {
            position: "right",
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            grid: { drawOnChartArea: false },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("titolo-studio-chart") && hasTitolo && state.charts.titoloStudio) {
    state.charts.titoloStudio.data.labels = titoloLabels;
    state.charts.titoloStudio.data.datasets[0].data = titoloCountsCompressed;
    state.charts.titoloStudio.data.datasets[0].rawCounts = titoloCounts;
    state.charts.titoloStudio.data.datasets[1].data = titoloAvg;
    state.charts.titoloStudio.data.datasets[2].data = titoloMedian;
    state.charts.titoloStudio.update();
  }

  const primoStipendioTitoloBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row["titolo-studio"]);
    if (!value) return;
    const ralValue = parseRal(row["primo-stipendio"]);
    if (ralValue === null) return;
    if (!primoStipendioTitoloBuckets[value]) {
      primoStipendioTitoloBuckets[value] = [];
    }
    primoStipendioTitoloBuckets[value].push(ralValue);
  });
  const primoTitoloStats = Object.keys(primoStipendioTitoloBuckets)
    .filter((label) => primoStipendioTitoloBuckets[label].length >= MIN_SAMPLE)
    .map((label) => {
      const trimmed = trimRal(primoStipendioTitoloBuckets[label]);
      const stats = computeStats(trimmed, false);
      return {
        label,
        avg: stats.avg,
        median: stats.median
      };
    })
    .sort((a, b) => (a.avg ?? 0) - (b.avg ?? 0));

  const primoTitoloLabels = primoTitoloStats.map((item) => item.label);
  const primoTitoloAvg = primoTitoloStats.map((item) => item.avg);
  const primoTitoloMedian = primoTitoloStats.map((item) => item.median);
  const hasPrimoTitolo = primoTitoloLabels.length > 0;
  if (isReady("primo-stipendio-titolo-chart")) {
    setChartEmpty("primo-stipendio-titolo-chart", !hasPrimoTitolo);
    if (!hasPrimoTitolo) {
      if (state.charts.primoTitolo) {
        state.charts.primoTitolo.destroy();
        state.charts.primoTitolo = null;
      }
    }
  }

  if (isReady("primo-stipendio-titolo-chart") && hasPrimoTitolo && !state.charts.primoTitolo) {
    state.charts.primoTitolo = new Chart(document.getElementById("primo-stipendio-titolo-chart"), {
      type: "bar",
      data: {
        labels: primoTitoloLabels,
        datasets: [
          {
            label: "RAL media",
            data: primoTitoloAvg,
            backgroundColor: "rgba(11, 122, 117, 0.45)",
            borderColor: "#0b7a75",
            borderWidth: 1
          },
          {
            label: "RAL mediana",
            data: primoTitoloMedian,
            backgroundColor: "rgba(255, 138, 0, 0.45)",
            borderColor: "#ff8a00",
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => `${context.dataset.label}: ${currency.format(context.parsed.y)}`
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257", maxRotation: 45, minRotation: 45 }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("primo-stipendio-titolo-chart") && hasPrimoTitolo && state.charts.primoTitolo) {
    state.charts.primoTitolo.data.labels = primoTitoloLabels;
    state.charts.primoTitolo.data.datasets[0].data = primoTitoloAvg;
    state.charts.primoTitolo.data.datasets[1].data = primoTitoloMedian;
    state.charts.primoTitolo.update();
  }

  const titoloBenessere = titoloLabels.map((label) => {
    const entries = filtered.filter((row) => normalizeValue(row["titolo-studio"]) === label);
    const soddisfazioneVals = trimOutliers(entries.map((row) => parseScale(row.soddisfazione)).filter((v) => v !== null));
    const stressVals = trimOutliers(entries.map((row) => parseScale(row.stress)).filter((v) => v !== null));
    const crescitaVals = trimOutliers(entries.map((row) => parseScale(row.crescita)).filter((v) => v !== null));
    return {
      label,
      soddisfazione: computeStats(soddisfazioneVals, false).avg,
      stress: computeStats(stressVals, false).avg,
      crescita: computeStats(crescitaVals, false).avg
    };
  });
  const titoloBenessereLabels = titoloBenessere.map((item) => item.label);
  const soddisfazioneTitolo = titoloBenessere.map((item) => scaleToPercent(item.soddisfazione));
  const stressTitolo = titoloBenessere.map((item) => scaleToPercent(item.stress));
  const crescitaTitolo = titoloBenessere.map((item) => scaleToPercent(item.crescita));
  const hasTitoloBenessere = titoloBenessereLabels.length > 0;
  if (isReady("titolo-benessere-chart")) {
    setChartEmpty("titolo-benessere-chart", !hasTitoloBenessere);
    if (!hasTitoloBenessere) {
      if (state.charts.titoloBenessere) {
        state.charts.titoloBenessere.destroy();
        state.charts.titoloBenessere = null;
      }
    }
  }

  if (isReady("titolo-benessere-chart") && hasTitoloBenessere && !state.charts.titoloBenessere) {
    state.charts.titoloBenessere = new Chart(document.getElementById("titolo-benessere-chart"), {
      type: "bar",
      data: {
        labels: titoloBenessereLabels,
        datasets: [
          {
            label: "Soddisfazione",
            data: soddisfazioneTitolo,
            backgroundColor: "rgba(11, 122, 117, 0.55)",
            borderColor: "#0b7a75",
            borderWidth: 1
          },
          {
            label: "Stress",
            data: stressTitolo,
            backgroundColor: "rgba(239, 68, 68, 0.55)",
            borderColor: "#ef4444",
            borderWidth: 1
          },
          {
            label: "Prospettive di crescita",
            data: crescitaTitolo,
            backgroundColor: "rgba(245, 158, 11, 0.55)",
            borderColor: "#f59e0b",
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => `${context.dataset.label}: ${numberFormat.format(context.parsed.y)}%`
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257", maxRotation: 45, minRotation: 45, autoSkip: false }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => `${numberFormat.format(value)}%`
            },
            beginAtZero: true,
            max: 100,
            title: { display: true, text: "Percentuale" }
          }
        }
      }
    });
  } else if (isReady("titolo-benessere-chart") && hasTitoloBenessere && state.charts.titoloBenessere) {
    state.charts.titoloBenessere.data.labels = titoloBenessereLabels;
    state.charts.titoloBenessere.data.datasets[0].data = soddisfazioneTitolo;
    state.charts.titoloBenessere.data.datasets[1].data = stressTitolo;
    state.charts.titoloBenessere.data.datasets[2].data = crescitaTitolo;
    state.charts.titoloBenessere.update();
  }

  const ambitoStudioBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row["ambito-studio"]);
    if (!value) return;
    const ralValue = parseRal(row.ral);
    if (!ambitoStudioBuckets[value]) {
      ambitoStudioBuckets[value] = [];
    }
    if (ralValue !== null) {
      ambitoStudioBuckets[value].push(ralValue);
    }
  });
  const ambitoStudioStats = Object.keys(ambitoStudioBuckets)
    .filter((label) => ambitoStudioBuckets[label].length >= MIN_SAMPLE)
    .map((label) => {
      const trimmed = trimRal(ambitoStudioBuckets[label]);
      const stats = computeStats(trimmed, false);
      return {
        label,
        count: trimmed.length,
        avg: stats.avg,
        median: stats.median
      };
    })
    .sort((a, b) => (a.avg ?? 0) - (b.avg ?? 0));

  const ambitoStudioLabels = ambitoStudioStats.map((item) => item.label);
  const ambitoStudioCounts = ambitoStudioStats.map((item) => item.count);
  const ambitoStudioCountsCompressed = ambitoStudioCounts.map((value) => Math.sqrt(value));
  const ambitoStudioAvg = ambitoStudioStats.map((item) => item.avg);
  const ambitoStudioMedian = ambitoStudioStats.map((item) => item.median);
  const hasAmbitoStudio = ambitoStudioLabels.length > 0;
  if (isReady("ambito-studio-chart")) {
    setChartEmpty("ambito-studio-chart", !hasAmbitoStudio);
    if (!hasAmbitoStudio) {
      if (state.charts.ambitoStudio) {
        state.charts.ambitoStudio.destroy();
        state.charts.ambitoStudio = null;
      }
    }
  }

  if (isReady("ambito-studio-chart") && hasAmbitoStudio && !state.charts.ambitoStudio) {
    state.charts.ambitoStudio = new Chart(document.getElementById("ambito-studio-chart"), {
      type: "bar",
      data: {
        labels: ambitoStudioLabels,
        datasets: [
          {
            label: "Risposte",
            data: ambitoStudioCountsCompressed,
            rawCounts: ambitoStudioCounts,
            backgroundColor: "rgba(59, 130, 246, 0.35)",
            borderColor: "#3b82f6",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: "RAL media",
            data: ambitoStudioAvg,
            borderColor: "#0b7a75",
            backgroundColor: "rgba(11, 122, 117, 0.15)",
            pointBackgroundColor: "#0b7a75",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          },
          {
            label: "RAL mediana",
            data: ambitoStudioMedian,
            borderColor: "#ff8a00",
            backgroundColor: "rgba(255, 138, 0, 0.15)",
            pointBackgroundColor: "#ff8a00",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === "Risposte") {
                  const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                  return `Risposte: ${countFormat.format(raw)}`;
                }
                return `${context.dataset.label}: ${currency.format(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257", maxRotation: 45, minRotation: 45 }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => Math.round(value * value)
            },
            beginAtZero: true,
            title: { display: true, text: "Risposte" }
          },
          yRal: {
            position: "right",
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            grid: { drawOnChartArea: false },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("ambito-studio-chart") && hasAmbitoStudio && state.charts.ambitoStudio) {
    state.charts.ambitoStudio.data.labels = ambitoStudioLabels;
    state.charts.ambitoStudio.data.datasets[0].data = ambitoStudioCountsCompressed;
    state.charts.ambitoStudio.data.datasets[0].rawCounts = ambitoStudioCounts;
    state.charts.ambitoStudio.data.datasets[1].data = ambitoStudioAvg;
    state.charts.ambitoStudio.data.datasets[2].data = ambitoStudioMedian;
    state.charts.ambitoStudio.update();
  }

  const primoStipendioBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row["ambito-studio"]);
    if (!value) return;
    const ralValue = parseRal(row["primo-stipendio"]);
    if (ralValue === null) return;
    if (!primoStipendioBuckets[value]) {
      primoStipendioBuckets[value] = [];
    }
    primoStipendioBuckets[value].push(ralValue);
  });
  const primoStipendioStats = Object.keys(primoStipendioBuckets)
    .filter((label) => primoStipendioBuckets[label].length >= MIN_SAMPLE)
    .map((label) => {
      const trimmed = trimRal(primoStipendioBuckets[label]);
      const stats = computeStats(trimmed, false);
      return {
        label,
        avg: stats.avg,
        median: stats.median
      };
    })
    .sort((a, b) => (a.avg ?? 0) - (b.avg ?? 0));

  const primoStipendioLabels = primoStipendioStats.map((item) => item.label);
  const primoStipendioAvg = primoStipendioStats.map((item) => item.avg);
  const primoStipendioMedian = primoStipendioStats.map((item) => item.median);
  const hasPrimoStipendio = primoStipendioLabels.length > 0;
  if (isReady("primo-stipendio-chart")) {
    setChartEmpty("primo-stipendio-chart", !hasPrimoStipendio);
    if (!hasPrimoStipendio) {
      if (state.charts.primoStipendio) {
        state.charts.primoStipendio.destroy();
        state.charts.primoStipendio = null;
      }
    }
  }

  if (isReady("primo-stipendio-chart") && hasPrimoStipendio && !state.charts.primoStipendio) {
    state.charts.primoStipendio = new Chart(document.getElementById("primo-stipendio-chart"), {
      type: "bar",
      data: {
        labels: primoStipendioLabels,
        datasets: [
          {
            label: "RAL media",
            data: primoStipendioAvg,
            backgroundColor: "rgba(11, 122, 117, 0.45)",
            borderColor: "#0b7a75",
            borderWidth: 1
          },
          {
            label: "RAL mediana",
            data: primoStipendioMedian,
            backgroundColor: "rgba(255, 138, 0, 0.45)",
            borderColor: "#ff8a00",
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => `${context.dataset.label}: ${currency.format(context.parsed.y)}`
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257", maxRotation: 45, minRotation: 45 }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("primo-stipendio-chart") && hasPrimoStipendio && state.charts.primoStipendio) {
    state.charts.primoStipendio.data.labels = primoStipendioLabels;
    state.charts.primoStipendio.data.datasets[0].data = primoStipendioAvg;
    state.charts.primoStipendio.data.datasets[1].data = primoStipendioMedian;
    state.charts.primoStipendio.update();
  }

  const livelloBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row.livello);
    if (!value) return;
    const ralValue = parseRal(row.ral);
    if (!livelloBuckets[value]) {
      livelloBuckets[value] = [];
    }
    if (ralValue !== null) {
      livelloBuckets[value].push(ralValue);
    }
  });
  const livelloLabels = [
    ...LIVELLO_ORDER.filter((label) => livelloBuckets[label]?.length >= MIN_SAMPLE),
    ...Object.keys(livelloBuckets).filter(
      (label) => !LIVELLO_ORDER.includes(label) && livelloBuckets[label]?.length >= MIN_SAMPLE
    )
  ];
  if (livelloLabels.length) {
    const livelloDebug = livelloLabels.map((label) => {
      const stats = computeStats(livelloBuckets[label] || []);
      return {
        livello: label,
        count: livelloBuckets[label]?.length || 0,
        avg: stats.avg,
        median: stats.median
      };
    });
    console.table(livelloDebug);
  }
  const livelloTrimmed = livelloLabels.map((label) => trimRal(livelloBuckets[label]));
  const livelloCounts = livelloTrimmed.map((values) => values.length);
  const livelloCountsCompressed = livelloCounts.map((value) => Math.sqrt(value));
  const livelloAvg = livelloTrimmed.map((values) => computeStats(values, false).avg);
  const livelloMedian = livelloTrimmed.map((values) => computeStats(values, false).median);
  const hasLivello = livelloLabels.length > 0;
  if (isReady("livello-chart")) {
    setChartEmpty("livello-chart", !hasLivello);
    if (!hasLivello) {
      if (state.charts.livello) {
        state.charts.livello.destroy();
        state.charts.livello = null;
      }
    }
  }

  if (isReady("livello-chart") && hasLivello && !state.charts.livello) {
    state.charts.livello = new Chart(document.getElementById("livello-chart"), {
      type: "bar",
      data: {
        labels: livelloLabels,
        datasets: [
          {
            label: "Risposte",
            data: livelloCountsCompressed,
            rawCounts: livelloCounts,
            backgroundColor: "rgba(153, 102, 255, 0.35)",
            borderColor: "#9966ff",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: "RAL media",
            data: livelloAvg,
            borderColor: "#0b7a75",
            backgroundColor: "rgba(11, 122, 117, 0.15)",
            pointBackgroundColor: "#0b7a75",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          },
          {
            label: "RAL mediana",
            data: livelloMedian,
            borderColor: "#ff8a00",
            backgroundColor: "rgba(255, 138, 0, 0.15)",
            pointBackgroundColor: "#ff8a00",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === "Risposte") {
                  const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                  return `Risposte: ${countFormat.format(raw)}`;
                }
                return `${context.dataset.label}: ${currency.format(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257" }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => Math.round(value * value)
            },
            beginAtZero: true,
            title: { display: true, text: "Risposte" }
          },
          yRal: {
            position: "right",
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            grid: { drawOnChartArea: false },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("livello-chart") && hasLivello && state.charts.livello) {
    state.charts.livello.data.labels = livelloLabels;
    state.charts.livello.data.datasets[0].data = livelloCountsCompressed;
    state.charts.livello.data.datasets[0].rawCounts = livelloCounts;
    state.charts.livello.data.datasets[1].data = livelloAvg;
    state.charts.livello.data.datasets[2].data = livelloMedian;
    state.charts.livello.update();
  }

  const dimensioneBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row.dimensione);
    if (!value) return;
    const ralValue = parseRal(row.ral);
    if (!dimensioneBuckets[value]) {
      dimensioneBuckets[value] = [];
    }
    if (ralValue !== null) {
      dimensioneBuckets[value].push(ralValue);
    }
  });
  const dimensioneLabels = [
    ...DIMENSIONE_ORDER.filter((label) => dimensioneBuckets[label]?.length >= MIN_SAMPLE),
    ...Object.keys(dimensioneBuckets).filter(
      (label) => !DIMENSIONE_ORDER.includes(label) && dimensioneBuckets[label]?.length >= MIN_SAMPLE
    )
  ];
  const dimensioneTrimmed = dimensioneLabels.map((label) => trimRal(dimensioneBuckets[label]));
  const dimensioneCounts = dimensioneTrimmed.map((values) => values.length);
  const dimensioneCountsCompressed = dimensioneCounts.map((value) => Math.sqrt(value));
  const dimensioneAvg = dimensioneTrimmed.map((values) => computeStats(values, false).avg);
  const dimensioneMedian = dimensioneTrimmed.map((values) => computeStats(values, false).median);
  const hasDimensione = dimensioneLabels.length > 0;
  if (isReady("dimensione-chart")) {
    setChartEmpty("dimensione-chart", !hasDimensione);
    if (!hasDimensione) {
      if (state.charts.dimensione) {
        state.charts.dimensione.destroy();
        state.charts.dimensione = null;
      }
    }
  }

  if (isReady("dimensione-chart") && hasDimensione && !state.charts.dimensione) {
    state.charts.dimensione = new Chart(document.getElementById("dimensione-chart"), {
      type: "bar",
      data: {
        labels: dimensioneLabels,
        datasets: [
          {
            label: "Risposte",
            data: dimensioneCountsCompressed,
            rawCounts: dimensioneCounts,
            backgroundColor: "rgba(59, 130, 246, 0.35)",
            borderColor: "#3b82f6",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: "RAL media",
            data: dimensioneAvg,
            borderColor: "#0b7a75",
            backgroundColor: "rgba(11, 122, 117, 0.15)",
            pointBackgroundColor: "#0b7a75",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          },
          {
            label: "RAL mediana",
            data: dimensioneMedian,
            borderColor: "#ff8a00",
            backgroundColor: "rgba(255, 138, 0, 0.15)",
            pointBackgroundColor: "#ff8a00",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === "Risposte") {
                  const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                  return `Risposte: ${countFormat.format(raw)}`;
                }
                return `${context.dataset.label}: ${currency.format(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257" }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => Math.round(value * value)
            },
            beginAtZero: true,
            title: { display: true, text: "Risposte" }
          },
          yRal: {
            position: "right",
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            grid: { drawOnChartArea: false },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("dimensione-chart") && hasDimensione && state.charts.dimensione) {
    state.charts.dimensione.data.labels = dimensioneLabels;
    state.charts.dimensione.data.datasets[0].data = dimensioneCountsCompressed;
    state.charts.dimensione.data.datasets[0].rawCounts = dimensioneCounts;
    state.charts.dimensione.data.datasets[1].data = dimensioneAvg;
    state.charts.dimensione.data.datasets[2].data = dimensioneMedian;
    state.charts.dimensione.update();
  }

  const settoreBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row.settore);
    if (!value) return;
    const ralValue = parseRal(row.ral);
    if (!settoreBuckets[value]) {
      settoreBuckets[value] = [];
    }
    if (ralValue !== null) {
      settoreBuckets[value].push(ralValue);
    }
  });
  const settoreStats = Object.keys(settoreBuckets)
    .filter((label) => settoreBuckets[label].length >= MIN_SAMPLE)
    .map((label) => {
      const trimmed = trimRal(settoreBuckets[label]);
      const stats = computeStats(trimmed, false);
      return {
        label,
        count: trimmed.length,
        avg: stats.avg,
        median: stats.median
      };
    })
    .sort((a, b) => (a.avg ?? 0) - (b.avg ?? 0));

  const settoreLabels = settoreStats.map((item) => item.label);
  const settoreCounts = settoreStats.map((item) => item.count);
  const settoreCountsCompressed = settoreCounts.map((value) => Math.sqrt(value));
  const settoreAvg = settoreStats.map((item) => item.avg);
  const settoreMedian = settoreStats.map((item) => item.median);
  const hasSettore = settoreLabels.length > 0;
  if (isReady("settore-chart")) {
    setChartEmpty("settore-chart", !hasSettore);
    if (!hasSettore) {
      if (state.charts.settore) {
        state.charts.settore.destroy();
        state.charts.settore = null;
      }
    }
  }

  if (isReady("settore-chart") && hasSettore && !state.charts.settore) {
    state.charts.settore = new Chart(document.getElementById("settore-chart"), {
      type: "bar",
      data: {
        labels: settoreLabels,
        datasets: [
          {
            label: "Risposte",
            data: settoreCountsCompressed,
            rawCounts: settoreCounts,
            backgroundColor: "rgba(255, 159, 64, 0.35)",
            borderColor: "#ff9f40",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: "RAL media",
            data: settoreAvg,
            borderColor: "#0b7a75",
            backgroundColor: "rgba(11, 122, 117, 0.15)",
            pointBackgroundColor: "#0b7a75",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          },
          {
            label: "RAL mediana",
            data: settoreMedian,
            borderColor: "#ff8a00",
            backgroundColor: "rgba(255, 138, 0, 0.15)",
            pointBackgroundColor: "#ff8a00",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === "Risposte") {
                  const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                  return `Risposte: ${countFormat.format(raw)}`;
                }
                return `${context.dataset.label}: ${currency.format(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257", autoSkip: false, maxRotation: 45, minRotation: 45 }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => Math.round(value * value)
            },
            beginAtZero: true,
            title: { display: true, text: "Risposte" }
          },
          yRal: {
            position: "right",
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            grid: { drawOnChartArea: false },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("settore-chart") && hasSettore && state.charts.settore) {
    state.charts.settore.data.labels = settoreLabels;
    state.charts.settore.data.datasets[0].data = settoreCountsCompressed;
    state.charts.settore.data.datasets[0].rawCounts = settoreCounts;
    state.charts.settore.data.datasets[1].data = settoreAvg;
    state.charts.settore.data.datasets[2].data = settoreMedian;
    state.charts.settore.update();
  }

  const settoreBenessere = settoreStats.map((item) => {
    const entries = filtered.filter((row) => normalizeValue(row.settore) === item.label);
    const soddisfazioneVals = trimOutliers(entries.map((row) => parseScale(row.soddisfazione)).filter((v) => v !== null));
    const stressVals = trimOutliers(entries.map((row) => parseScale(row.stress)).filter((v) => v !== null));
    const crescitaVals = trimOutliers(entries.map((row) => parseScale(row.crescita)).filter((v) => v !== null));
    return {
      label: item.label,
      soddisfazione: computeStats(soddisfazioneVals, false).avg,
      stress: computeStats(stressVals, false).avg,
      crescita: computeStats(crescitaVals, false).avg
    };
  });

  const settoreBenessereLabels = settoreBenessere.map((item) => item.label);
  const soddisfazioneSeries = settoreBenessere.map((item) => scaleToPercent(item.soddisfazione));
  const stressSeries = settoreBenessere.map((item) => scaleToPercent(item.stress));
  const crescitaSeries = settoreBenessere.map((item) => scaleToPercent(item.crescita));
  const hasSettoreBenessere = settoreBenessereLabels.length > 0;
  if (isReady("settore-benessere-chart")) {
    setChartEmpty("settore-benessere-chart", !hasSettoreBenessere);
    if (!hasSettoreBenessere) {
      if (state.charts.settoreBenessere) {
        state.charts.settoreBenessere.destroy();
        state.charts.settoreBenessere = null;
      }
    }
  }

  if (isReady("settore-benessere-chart") && hasSettoreBenessere && !state.charts.settoreBenessere) {
    state.charts.settoreBenessere = new Chart(document.getElementById("settore-benessere-chart"), {
      type: "bar",
      data: {
        labels: settoreBenessereLabels,
        datasets: [
          {
            label: "Soddisfazione",
            data: soddisfazioneSeries,
            backgroundColor: "rgba(11, 122, 117, 0.55)",
            borderColor: "#0b7a75",
            borderWidth: 1
          },
          {
            label: "Stress",
            data: stressSeries,
            backgroundColor: "rgba(239, 68, 68, 0.55)",
            borderColor: "#ef4444",
            borderWidth: 1
          },
          {
            label: "Prospettive di crescita",
            data: crescitaSeries,
            backgroundColor: "rgba(245, 158, 11, 0.55)",
            borderColor: "#f59e0b",
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => `${context.dataset.label}: ${numberFormat.format(context.parsed.y)}%`
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257", maxRotation: 45, minRotation: 45, autoSkip: false }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => `${numberFormat.format(value)}%`
            },
            beginAtZero: true,
            max: 100,
            title: { display: true, text: "Percentuale" }
          }
        }
      }
    });
  } else if (isReady("settore-benessere-chart") && hasSettoreBenessere && state.charts.settoreBenessere) {
    state.charts.settoreBenessere.data.labels = settoreBenessereLabels;
    state.charts.settoreBenessere.data.datasets[0].data = soddisfazioneSeries;
    state.charts.settoreBenessere.data.datasets[1].data = stressSeries;
    state.charts.settoreBenessere.data.datasets[2].data = crescitaSeries;
    state.charts.settoreBenessere.update();
  }

  const ambitoBuckets = {};
  filtered.forEach((row) => {
    const value = normalizeValue(row.ambito);
    if (!value) return;
    const ralValue = parseRal(row.ral);
    if (!ambitoBuckets[value]) {
      ambitoBuckets[value] = [];
    }
    if (ralValue !== null) {
      ambitoBuckets[value].push(ralValue);
    }
  });
  const ambitoStats = Object.keys(ambitoBuckets)
    .filter((label) => ambitoBuckets[label].length >= MIN_SAMPLE)
    .map((label) => {
      const trimmed = trimRal(ambitoBuckets[label]);
      const stats = computeStats(trimmed, false);
      return {
        label,
        count: trimmed.length,
        avg: stats.avg,
        median: stats.median
      };
    })
    .sort((a, b) => (a.avg ?? 0) - (b.avg ?? 0));

  const ambitoLabels = ambitoStats.map((item) => item.label);
  const ambitoCounts = ambitoStats.map((item) => item.count);
  const ambitoCountsCompressed = ambitoCounts.map((value) => Math.sqrt(value));
  const ambitoAvg = ambitoStats.map((item) => item.avg);
  const ambitoMedian = ambitoStats.map((item) => item.median);
  const hasAmbito = ambitoLabels.length > 0;
  if (isReady("ambito-chart")) {
    setChartEmpty("ambito-chart", !hasAmbito);
    if (!hasAmbito) {
      if (state.charts.ambito) {
        state.charts.ambito.destroy();
        state.charts.ambito = null;
      }
    }
  }

  if (isReady("ambito-chart") && hasAmbito && !state.charts.ambito) {
    state.charts.ambito = new Chart(document.getElementById("ambito-chart"), {
      type: "bar",
      data: {
        labels: ambitoLabels,
        datasets: [
          {
            label: "Risposte",
            data: ambitoCountsCompressed,
            rawCounts: ambitoCounts,
            backgroundColor: "rgba(79, 70, 229, 0.35)",
            borderColor: "#4f46e5",
            borderWidth: 1,
            yAxisID: "y"
          },
          {
            label: "RAL media",
            data: ambitoAvg,
            borderColor: "#0b7a75",
            backgroundColor: "rgba(11, 122, 117, 0.15)",
            pointBackgroundColor: "#0b7a75",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          },
          {
            label: "RAL mediana",
            data: ambitoMedian,
            borderColor: "#ff8a00",
            backgroundColor: "rgba(255, 138, 0, 0.15)",
            pointBackgroundColor: "#ff8a00",
            borderWidth: 2,
            tension: 0.3,
            type: "line",
            yAxisID: "yRal"
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.dataset.label === "Risposte") {
                  const raw = context.dataset.rawCounts?.[context.dataIndex] ?? context.parsed.y;
                  return `Risposte: ${countFormat.format(raw)}`;
                }
                return `${context.dataset.label}: ${currency.format(context.parsed.y)}`;
              }
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257", maxRotation: 45, minRotation: 45 }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => Math.round(value * value)
            },
            beginAtZero: true,
            title: { display: true, text: "Risposte" }
          },
          yRal: {
            position: "right",
            ticks: {
              color: "#6b6257",
              callback: (value) => currency.format(value)
            },
            grid: { drawOnChartArea: false },
            beginAtZero: true,
            title: { display: true, text: "RAL (€)" }
          }
        }
      }
    });
  } else if (isReady("ambito-chart") && hasAmbito && state.charts.ambito) {
    state.charts.ambito.data.labels = ambitoLabels;
    state.charts.ambito.data.datasets[0].data = ambitoCountsCompressed;
    state.charts.ambito.data.datasets[0].rawCounts = ambitoCounts;
    state.charts.ambito.data.datasets[1].data = ambitoAvg;
    state.charts.ambito.data.datasets[2].data = ambitoMedian;
    state.charts.ambito.update();
  }

  const ambitoBenessere = ambitoStats.map((item) => {
    const entries = filtered.filter((row) => normalizeValue(row.ambito) === item.label);
    const soddisfazioneVals = trimOutliers(entries.map((row) => parseScale(row.soddisfazione)).filter((v) => v !== null));
    const stressVals = trimOutliers(entries.map((row) => parseScale(row.stress)).filter((v) => v !== null));
    const crescitaVals = trimOutliers(entries.map((row) => parseScale(row.crescita)).filter((v) => v !== null));
    return {
      label: item.label,
      soddisfazione: computeStats(soddisfazioneVals, false).avg,
      stress: computeStats(stressVals, false).avg,
      crescita: computeStats(crescitaVals, false).avg
    };
  });

  const ambitoBenessereLabels = ambitoBenessere.map((item) => item.label);
  const soddisfazioneAmbito = ambitoBenessere.map((item) => scaleToPercent(item.soddisfazione));
  const stressAmbito = ambitoBenessere.map((item) => scaleToPercent(item.stress));
  const crescitaAmbito = ambitoBenessere.map((item) => scaleToPercent(item.crescita));
  const hasAmbitoBenessere = ambitoBenessereLabels.length > 0;
  if (isReady("ambito-benessere-chart")) {
    setChartEmpty("ambito-benessere-chart", !hasAmbitoBenessere);
    if (!hasAmbitoBenessere) {
      if (state.charts.ambitoBenessere) {
        state.charts.ambitoBenessere.destroy();
        state.charts.ambitoBenessere = null;
      }
    }
  }

  if (isReady("ambito-benessere-chart") && hasAmbitoBenessere && !state.charts.ambitoBenessere) {
    state.charts.ambitoBenessere = new Chart(document.getElementById("ambito-benessere-chart"), {
      type: "bar",
      data: {
        labels: ambitoBenessereLabels,
        datasets: [
          {
            label: "Soddisfazione",
            data: soddisfazioneAmbito,
            backgroundColor: "rgba(11, 122, 117, 0.55)",
            borderColor: "#0b7a75",
            borderWidth: 1
          },
          {
            label: "Stress",
            data: stressAmbito,
            backgroundColor: "rgba(239, 68, 68, 0.55)",
            borderColor: "#ef4444",
            borderWidth: 1
          },
          {
            label: "Prospettive di crescita",
            data: crescitaAmbito,
            backgroundColor: "rgba(245, 158, 11, 0.55)",
            borderColor: "#f59e0b",
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: true },
          tooltip: {
            callbacks: {
              label: (context) => `${context.dataset.label}: ${numberFormat.format(context.parsed.y)}%`
            }
          }
        },
        scales: {
          x: {
            ticks: { color: "#6b6257", maxRotation: 45, minRotation: 45, autoSkip: false }
          },
          y: {
            ticks: {
              color: "#6b6257",
              callback: (value) => `${numberFormat.format(value)}%`
            },
            beginAtZero: true,
            max: 100,
            title: { display: true, text: "Percentuale" }
          }
        }
      }
    });
  } else if (isReady("ambito-benessere-chart") && hasAmbitoBenessere && state.charts.ambitoBenessere) {
    state.charts.ambitoBenessere.data.labels = ambitoBenessereLabels;
    state.charts.ambitoBenessere.data.datasets[0].data = soddisfazioneAmbito;
    state.charts.ambitoBenessere.data.datasets[1].data = stressAmbito;
    state.charts.ambitoBenessere.data.datasets[2].data = crescitaAmbito;
    state.charts.ambitoBenessere.update();
  }

  if (state.geo) {
    const regionBuckets = {};
    filtered.forEach((row) => {
      const value = normalizeValue(row.regione);
      if (!value) return;
      const ralValue = parseRal(row.ral);
      if (!regionBuckets[value]) {
        regionBuckets[value] = [];
      }
      if (ralValue !== null) {
        regionBuckets[value].push(ralValue);
      }
    });

    const regionDataAvg = state.geo.features.map((feature) => {
      const name = feature.properties.reg_name;
      const values = regionBuckets[name] || [];
      const trimmed = trimRal(values);
      if (trimmed.length < MIN_SAMPLE) {
        return { feature, value: null, count: values.length, name };
      }
      const stats = computeStats(trimmed, false);
      return { feature, value: stats.avg, count: trimmed.length, name };
    });

    const regionDataMedian = state.geo.features.map((feature) => {
      const name = feature.properties.reg_name;
      const values = regionBuckets[name] || [];
      const trimmed = trimRal(values);
      if (trimmed.length < MIN_SAMPLE) {
        return { feature, value: null, count: values.length, name };
      }
      const stats = computeStats(trimmed, false);
      return { feature, value: stats.median, count: trimmed.length, name };
    });

    const baseMapOptions = {
      responsive: true,
      layout: {
        padding: 0
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              const raw = context.raw || {};
              if (!raw.value) {
                return `${raw.name}: campione insufficiente`;
              }
              return `${raw.name}: ${currency.format(raw.value)} (n=${countFormat.format(raw.count)})`;
            }
          }
        }
      },
      scales: {
        projection: {
          axis: "x",
          projection: "mercator"
        },
        color: {
          axis: "x",
          quantize: 5
        }
      }
    };

    const hasRegionAvg = regionDataAvg.some((item) => item.value !== null);
    const hasRegionMedian = regionDataMedian.some((item) => item.value !== null);
    if (isReady("regione-media-chart")) {
      setChartEmpty("regione-media-chart", !hasRegionAvg);
      if (!hasRegionAvg) {
        if (state.charts.regioneAvg) {
          state.charts.regioneAvg.destroy();
          state.charts.regioneAvg = null;
        }
      }
    }
    if (isReady("regione-mediana-chart")) {
      setChartEmpty("regione-mediana-chart", !hasRegionMedian);
      if (!hasRegionMedian) {
        if (state.charts.regioneMedian) {
          state.charts.regioneMedian.destroy();
          state.charts.regioneMedian = null;
        }
      }
    }

    if (isReady("regione-media-chart") && hasRegionAvg && !state.charts.regioneAvg) {
      state.charts.regioneAvg = new Chart(document.getElementById("regione-media-chart"), {
        type: "choropleth",
        data: {
          labels: state.geo.features.map((f) => f.properties.reg_name),
          datasets: [{
            label: "RAL media",
            data: regionDataAvg,
            outline: state.geo.features
          }]
        },
        options: baseMapOptions
      });
    } else if (isReady("regione-media-chart") && hasRegionAvg && state.charts.regioneAvg) {
      state.charts.regioneAvg.data.datasets[0].data = regionDataAvg;
      state.charts.regioneAvg.update();
    }

    if (isReady("regione-mediana-chart") && hasRegionMedian && !state.charts.regioneMedian) {
      state.charts.regioneMedian = new Chart(document.getElementById("regione-mediana-chart"), {
        type: "choropleth",
        data: {
          labels: state.geo.features.map((f) => f.properties.reg_name),
          datasets: [{
            label: "RAL mediana",
            data: regionDataMedian,
            outline: state.geo.features
          }]
        },
        options: baseMapOptions
      });
    } else if (isReady("regione-mediana-chart") && hasRegionMedian && state.charts.regioneMedian) {
      state.charts.regioneMedian.data.datasets[0].data = regionDataMedian;
      state.charts.regioneMedian.update();
    }
  }

}

function applyFilters() {
  const filters = {
    genere: document.getElementById("filter-genere").value,
    regione: document.getElementById("filter-regione").value,
    settore: document.getElementById("filter-settore").value,
    seniority: document.getElementById("filter-seniority").value,
    rapporto: document.getElementById("filter-rapporto").value,
    modalita: document.getElementById("filter-modalita").value,
    eta: document.getElementById("filter-eta").value,
    esperienza: document.getElementById("filter-esperienza").value,
    ralMin: Number(document.getElementById("filter-ral-min").value || 0),
    ralMax: Number(document.getElementById("filter-ral-max").value || Infinity)
  };

  state.filtered = state.raw.filter((row) => {
    if (filters.genere && normalizeValue(row.genere) !== filters.genere) return false;
    if (filters.regione && normalizeValue(row.regione) !== filters.regione) return false;
    if (filters.settore && normalizeValue(row.settore) !== filters.settore) return false;
    if (filters.seniority && normalizeValue(row.seniority) !== filters.seniority) return false;
    if (filters.rapporto && normalizeValue(row.rapporto) !== filters.rapporto) return false;
    if (filters.modalita && normalizeValue(row["modalita-lavoro"]) !== filters.modalita) return false;
    if (filters.eta && normalizeValue(row.eta) !== filters.eta) return false;
    if (filters.esperienza && normalizeValue(row.esperienza) !== filters.esperienza) return false;

    const ralValue = parseRal(row.ral);
    if (ralValue === null) return false;
    if (ralValue < filters.ralMin || ralValue > filters.ralMax) return false;
    return true;
  });

  const ralValues = state.filtered.map((row) => parseRal(row.ral)).filter((value) => value !== null);
  const ralTrimmed = trimRal(ralValues);
  const stats = computeStats(ralTrimmed, false);

  document.getElementById("filtered-count").textContent = ralTrimmed.length.toLocaleString("it-IT");
  document.getElementById("filtered-avg").textContent = stats.avg ? currency.format(stats.avg) : "--";
  document.getElementById("filtered-median").textContent = stats.median ? currency.format(stats.median) : "--";
  document.getElementById("filtered-range").textContent = stats.min !== null ? `${currency.format(stats.min)} - ${currency.format(stats.max)}` : "--";

  updateCharts(state.filtered);
}

function attachFilterListeners() {
  document.querySelectorAll(".filters select, .filters input").forEach((input) => {
    input.addEventListener("change", applyFilters);
  });
  document.getElementById("reset-filters").addEventListener("click", () => {
    document.querySelectorAll(".filters select").forEach((select) => {
      select.value = "";
    });
    document.getElementById("filter-ral-min").value = "";
    document.getElementById("filter-ral-max").value = "";
    applyFilters();
  });
}

function initOverviewToggle() {
  const toggle = document.getElementById("overview-toggle");
  const details = document.getElementById("overview-details");
  if (!toggle || !details) return;

  const mobileQuery = window.matchMedia("(max-width: 640px)");

  const setExpanded = (expanded) => {
    details.hidden = !expanded;
    toggle.setAttribute("aria-expanded", String(expanded));
    toggle.setAttribute("aria-label", expanded ? "Nascondi dettagli" : "Mostra dettagli");
  };

  const syncLayout = () => {
    if (mobileQuery.matches) {
      setExpanded(false);
      return;
    }

    details.hidden = false;
    toggle.setAttribute("aria-expanded", "true");
    toggle.setAttribute("aria-label", "Nascondi dettagli");
  };

  toggle.addEventListener("click", () => {
    setExpanded(toggle.getAttribute("aria-expanded") !== "true");
  });

  syncLayout();
  mobileQuery.addEventListener("change", syncLayout);
}

function initFiltersToggle() {
  const toggle = document.getElementById("filters-toggle");
  const details = document.getElementById("filters-details");
  if (!toggle || !details) return;

  const mobileQuery = window.matchMedia("(max-width: 768px)");

  const setExpanded = (expanded) => {
    details.hidden = !expanded;
    toggle.setAttribute("aria-expanded", String(expanded));
    toggle.setAttribute("aria-label", expanded ? "Nascondi filtri" : "Mostra filtri");
  };

  const syncLayout = () => {
    if (mobileQuery.matches) {
      setExpanded(false);
      return;
    }

    details.hidden = false;
    toggle.setAttribute("aria-expanded", "true");
    toggle.setAttribute("aria-label", "Nascondi filtri");
  };

  toggle.addEventListener("click", () => {
    setExpanded(toggle.getAttribute("aria-expanded") !== "true");
  });

  syncLayout();
  mobileQuery.addEventListener("change", syncLayout);
}

function hydrateOverview(data) {
  const timestamps = data.map((row) => row.timestamp).filter((value) => value);
  const lastUpdated = timestamps.length ? timestamps.sort().slice(-1)[0] : null;
  const ralValues = data.map((row) => parseRal(row.ral)).filter((value) => value !== null);
  const ralTrimmed = trimRal(ralValues);
  const stats = computeStats(ralTrimmed, false);
  const soddisfazioneValues = data.map((row) => parseScale(row.soddisfazione)).filter((value) => value !== null);
  const stressValues = data.map((row) => parseScale(row.stress)).filter((value) => value !== null);
  const crescitaValues = data.map((row) => parseScale(row.crescita)).filter((value) => value !== null);
  const soddisfazioneTrimmed = trimOutliers(soddisfazioneValues);
  const stressTrimmed = trimOutliers(stressValues);
  const soddisfazioneStats = computeStats(soddisfazioneTrimmed, false);
  const stressStats = computeStats(stressTrimmed, false);
  const crescitaTrimmed = trimOutliers(crescitaValues);
  const crescitaStats = computeStats(crescitaTrimmed, false);

  document.getElementById("last-updated").textContent = formatDate(lastUpdated);
  document.getElementById("total-count").textContent = ralTrimmed.length.toLocaleString("it-IT");
  const rawCountEl = document.getElementById("raw-count");
  if (rawCountEl) {
    rawCountEl.textContent = ralValues.length.toLocaleString("it-IT");
  }
  const trimmedCountEl = document.getElementById("trimmed-count");
  if (trimmedCountEl) {
    trimmedCountEl.textContent = ralTrimmed.length.toLocaleString("it-IT");
  }
  document.getElementById("avg-ral").textContent = stats.avg ? currency.format(stats.avg) : "--";
  document.getElementById("median-ral").textContent = stats.median ? currency.format(stats.median) : "--";
  document.getElementById("avg-soddisfazione").textContent = soddisfazioneStats.avg ? `${numberFormat.format(scaleToPercent(soddisfazioneStats.avg))}%` : "--";
  document.getElementById("avg-stress").textContent = stressStats.avg ? `${numberFormat.format(scaleToPercent(stressStats.avg))}%` : "--";
  document.getElementById("avg-crescita").textContent = crescitaStats.avg ? `${numberFormat.format(scaleToPercent(crescitaStats.avg))}%` : "--";
}

function populateFilters(data) {
  setOptions(document.getElementById("filter-genere"), uniqueValues(data, "genere"));
  setOptions(document.getElementById("filter-regione"), uniqueValues(data, "regione"));
  setOptions(document.getElementById("filter-settore"), uniqueValues(data, "settore"));
  setOptions(document.getElementById("filter-seniority"), uniqueValues(data, "seniority"));
  setOptions(document.getElementById("filter-rapporto"), uniqueValues(data, "rapporto"));
  setOptions(document.getElementById("filter-modalita"), uniqueValues(data, "modalita-lavoro"));
  setOptions(document.getElementById("filter-eta"), uniqueValues(data, "eta"));
  setOptionsOrdered(document.getElementById("filter-esperienza"), ESPERIENZA_ORDER, uniqueValues(data, "esperienza"));
}

function loadCsv() {
  return fetch(CSV_URL)
    .then((response) => {
      if (!response.ok) throw new Error("Impossibile caricare il dataset");
      return response.text();
    })
    .then((csvText) => new Promise((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: (result) => resolve(result.data),
        error: reject
      });
    }));
}

function loadGeoJson() {
  return fetch(GEOJSON_URL).then((response) => {
    if (!response.ok) throw new Error("Impossibile caricare la mappa regioni");
    return response.json();
  });
}

function ensureGeoChartRegistered() {
  const chartGeo = window.ChartGeo;
  if (!chartGeo || ensureGeoChartRegistered.done) return;
  Chart.register(
    chartGeo.ChoroplethController,
    chartGeo.ColorScale,
    chartGeo.GeoFeature,
    chartGeo.ProjectionScale
  );
  ensureGeoChartRegistered.done = true;
}

document.addEventListener("DOMContentLoaded", async () => {
  const spinner = document.getElementById("loading-spinner");
  const dashboardRoot = document.getElementById("dashboard-root");
  initOverviewToggle();
  initFiltersToggle();
  try {
    const [data, geo] = await Promise.all([loadCsv(), loadGeoJson()]);
    state.raw = data;
    state.geo = geo;

    ensureGeoChartRegistered();
    hydrateOverview(data);
    populateFilters(data);
    attachFilterListeners();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (entry.target?.id) {
            state.chartReady.add(entry.target.id);
            observer.unobserve(entry.target);
          }
        });
        if (state.filtered.length) {
          updateCharts(state.filtered);
        }
      },
      { rootMargin: "200px 0px" }
    );
    document.querySelectorAll("[data-lazy-chart]").forEach((el) => observer.observe(el));
    applyFilters();
  } catch (error) {
    console.error(error);
    const lastUpdated = document.getElementById("last-updated");
    if (lastUpdated) lastUpdated.textContent = "Errore";
  } finally {
    if (spinner) spinner.classList.add("hidden");
    if (dashboardRoot) dashboardRoot.style.display = "block";
  }
});
